import type { Board, ClassicGameState, Coord, Freqs, HighlightColors, Match, Puzzle, Stats, Tile } from "~/types";
import { Game } from "./game.svelte";
import localforage from "localforage";
import { resetBoard } from "~/algorithms/gameLogic";
import { sample } from "~/algorithms/letters";
import { shuffle, shuffleBoard } from "~/algorithms/shuffle";
import { seededRng } from "~/algorithms/rng";
import { dictionary } from "~/algorithms/dictionary.svelte";
import { coordToStr } from "~/board/utils";
import { PersistedState } from "runed";

export class ClassicGame extends Game<Tile> {
  static _mode = 'classic' as const;
  dims: Coord = [6, 7];

  streak = $state(0);
  streakLevel = $state(1);
  streakInterval = $state(5);

  async init() {
    await super.init();
    this.board = await resetBoard(this.dims);
    return this;
  }

  // can load from backend snapshot
  static async load(g?: Puzzle) {
    const game = await super.load(g) as ClassicGame;

    /* migrate from localforage to IDB if IDB has no record yet */
    if (game.db && await game.db.count(Game.gameKey) === 0) {
      const gameState = await localforage.getItem<ClassicGameState>('classic');
      if (gameState && !gameState.lost) {
        game.board = gameState.board;
        game.swaps = gameState.remainingSwaps;
        game.shuffles = gameState.shuffles;
        game.streak = gameState.streak;
        game.streakLevel = gameState.streakLevel;
        game.streakInterval = gameState.streakInterval;
        game.score = gameState.score;
        if (gameState.startedAt) game.startedAt = gameState.startedAt;
        if (gameState.latestWord) game.latestWord = gameState.latestWord;
        game.bulkInsertWords(gameState.words ?? []);
        game.bulkInsertTurns(gameState.turns ?? []);
        await game.save();
      }
      await localforage.removeItem('classic');
    }
    /* end migration */

    if (!game || game.lost) {
      await game.clear();
      return await new ClassicGame().init();
    }
    return game;
  }

  serialize() {
    return {
      ...super.serialize(),
      streak: this.streak,
      streakLevel: this.streakLevel,
      streakInterval: this.streakInterval,
    };
  }

  highlightWords(words: Match[]) {
    for (const word of words) {
      let highlight: HighlightColors = 'green';
      if (this.isFullLength(word)) {
        highlight = 'orange';
      } else if (word.intersectingIds.length) {
        for (const id of word.intersectingIds) {
          this.highlighted[id] = 'red';
        }
        highlight = 'purple';
      }
      for (const tile of word.word) {
        if (!this.highlighted[tile.id]) {
          this.highlighted[tile.id] = highlight;
        }
      }
    }
  }

  setMarqueeText(match: Match, chain: number) {
    if (match.score >= 50 || chain >= 6) {
      this.marquee = '🎆 UNBELIEVABLE!';
    } else if (match.score >= 40 || chain === 5 || match.word.length === 8) {
      this.marquee = '🎉 INCREDIBLE!';
    } else if (
      match.score >= 30 ||
      chain === 4 ||
      match.word.length === 7 ||
      match.intersection
    ) {
      this.marquee = '🎊 IMPRESSIVE!';
    } else if (match.score >= 20 || chain === 3 || match.word.length === 6) {
      this.marquee = '🥳 WOW!';
    } else if (match.score >= 10 || chain === 2 || match.word.length === 5) {
      this.marquee = '👍 NICE!';
    }
  };

  decrementSwaps(c1: Coord, c2: Coord) {
    this.swaps -= this.getSwapPenalty(c1, c2);
  }

  getSwapPenalty([i, j]: Coord, [i2, j2]: Coord) {
    // if tiles are adjacent deduct 1 swap
    // else deduct 2
    const distanceX = Math.abs(i - i2);
    const distanceY = Math.abs(j - j2);
    return Math.min(Math.max(distanceX, distanceY), 2);
  };

  async collapseBoard() {
    this.board = await this.replaceLetters(
      $state.snapshot(this.board),
      $state.snapshot(this.score),
      $state.snapshot(this.turns),
    );
  }

  // Override point for tutorials/tests that want deterministic refills.
  sampleLetter(board: Board, rng: () => number): [Freqs<string>, Tile] {
    return sample(board, this.turns, this.swaps, this.streakLevel, 1, rng);
  }

  async replaceLetters(
    board: Board,
    turn: number,
    score: number,
  ) {
    const rng = seededRng(this.id, turn + score);
    let cleared = board.map((col) =>
      col.reverse().filter((tile) => tile != undefined),
    );

    const coords: Coord[] = [];
    let freqs: Freqs<string>;
    const newLetters: string[] = [];
    for (let i = 0; i < board.length; i++) {
      for (let j = 0; j < board[0].length; j++) {
        if (cleared[i][j] == undefined) {
          const [stats, tile] = this.sampleLetter(cleared, rng);
          cleared[i][j] = tile;
          // column coord is inverted here
          coords.push([i, board[0].length - j - 1]);
          freqs = stats;
          newLetters.push(tile.letter);
        }
      }
    }
    // flip board back over
    cleared = cleared.map((col) => col.reverse());
  
    // reduce likihood of new words falling in from top of board
    for (let i = 0; i < 2; i++) {
      const { words } = await dictionary.findWords(cleared, 'classic');
      if (words.length === 0) {
        break;
      }
      for (const word of words) {
        if (
          coords.some((c) => word.coords.map(coordToStr).includes(coordToStr(c)))
        ) {
          const shuffled = shuffle(coords, rng);
          cleared = shuffleBoard(cleared, coords, shuffled);
          break;
        }
      }
    }
    return cleared;
  };

  isFullLength(w: Match) {
    return (w.axis === 'row' && w.word.length === this.board.length
    || w.axis === 'col' && w.word.length === this.board[0].length)
  }

  async saveStats() {
    const words = await this.getAll();
    const stats = new PersistedState<Stats>('stats', { stars: {} } as Stats);
    stats.current.gamesPlayed = (stats.current.gamesPlayed ?? 0) + 1;
    stats.current.totalWordsPlayed =
      (stats.current.totalWordsPlayed ?? 0) + this.words;
    if (!stats.current.stars) {
      stats.current.stars = {};
    }
    if (!stats.current.wordScoreFreqs) {
      stats.current.wordScoreFreqs = {};
    }
    if (!stats.current.wordLengths) {
      stats.current.wordLengths = {};
    }
  
    for (const word of words) {
      stats.current.wordScoreFreqs[word.score] =
        (stats.current.wordScoreFreqs[word.score] ?? 0) + 1;
      const wordLength = word.word.reduce(
        (total, t) => total + t.letter.length,
        0,
      );
      stats.current.wordLengths[wordLength] =
        (stats.current.wordLengths[wordLength] ?? 0) + 1;
    }
    return stats;
  };
}

